import React, { useState } from 'react';
import { useCollegePrepContext } from '../../contexts/CollegePrepContext';
import QuickAdd from './ProgramCollection/QuickAdd';

const ProgramCollection = () => {
  const { programs, addProgram, updateProgram, deleteProgram } = useCollegePrepContext();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [newProgram, setNewProgram] = useState({
    name: '',
    organization: '',
    category: '',
    description: '',
    deadline: '',
    duration: '',
    cost: '',
    competitiveness: 'moderate'
  });

  const handleAddProgram = (e) => {
    e.preventDefault();
    if (newProgram.name && newProgram.organization) {
      addProgram(newProgram);
      setNewProgram({
        name: '',
        organization: '',
        category: '',
        description: '',
        deadline: '',
        duration: '',
        cost: '',
        competitiveness: 'moderate'
      });
      setShowAddForm(false);
    }
  };

  const handleRatingChange = (programId, ratingType, value) => {
    updateProgram(programId, {
      personalRatings: {
        ...programs.find(p => p.id === programId)?.personalRatings,
        [ratingType]: value
      }
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'need-research': 'bg-orange-100 text-orange-600',
      'researching': 'bg-sky-100 text-sky-600',
      'researched': 'bg-emerald-100 text-emerald-600',
      'applied': 'bg-violet-100 text-violet-600'
    };
    return colors[status] || 'bg-stone-100 text-stone-600';
  };

  const getCompetitivenessColor = (level) => {
    const colors = {
      'low': 'bg-emerald-100 text-emerald-600',
      'moderate': 'bg-amber-100 text-amber-600',
      'high': 'bg-orange-100 text-orange-600',
      'ultra': 'bg-red-100 text-red-600'
    };
    return colors[level] || 'bg-stone-100 text-stone-600';
  };

  const getCompetitivenessLabel = (level) => {
    const labels = {
      'low': 'Safety',
      'moderate': 'Target',
      'high': 'Reach',
      'ultra': 'Ultra Reach'
    };
    return labels[level] || level;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      medical: '🏥',
      veterinary: '🐾',
      technology: '💻',
      socialJustice: '⚖️',
      research: '🔬',
      other: '📚'
    };
    return icons[category] || '📚';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Your Program Collection</h2>
          <p className="text-stone-600 mt-1">
            {programs.length} programs • Track your research and applications
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowQuickAdd(true)}
            className="px-4 py-2 border border-emerald-600 text-emerald-600 font-medium rounded-lg hover:bg-emerald-50"
          >
            Quick Add
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700"
          >
            Add Program
          </button>
        </div>
      </div>

      {/* Collection Overview */}
      {programs.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-emerald-800">Target List Overview</h3>
            <div className="flex gap-4 text-sm font-medium text-emerald-700">
              <span>{programs.filter(p => p.competitiveness === 'ultra' || p.competitiveness === 'high').length} Reaches</span>
              <span>{programs.filter(p => p.competitiveness === 'moderate').length} Targets</span>
              <span>{programs.filter(p => p.competitiveness === 'low').length} Safeties</span>
            </div>
          </div>
          <p className="text-sm text-emerald-600">
            Good balance! Make sure to research application requirements and deadlines for your reach programs.
          </p>
        </div>
      )}

      {/* Add Program Form */}
      {showAddForm && (
        <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New Program</h3>
          <form onSubmit={handleAddProgram} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Program Name</label>
                <input
                  type="text"
                  value={newProgram.name}
                  onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., Research Science Institute"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Organization</label>
                <input
                  type="text"
                  value={newProgram.organization}
                  onChange={(e) => setNewProgram({ ...newProgram, organization: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., MIT"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select
                  value={newProgram.category}
                  onChange={(e) => setNewProgram({ ...newProgram, category: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Select category</option>
                  <option value="medical">Medical & Healthcare</option>
                  <option value="veterinary">Veterinary & Animal Science</option>
                  <option value="technology">Technology & AI</option>
                  <option value="socialJustice">Social Justice & Policy</option>
                  <option value="research">Research</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Competitiveness</label>
                <select
                  value={newProgram.competitiveness}
                  onChange={(e) => setNewProgram({ ...newProgram, competitiveness: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="low">Safety</option>
                  <option value="moderate">Target</option>
                  <option value="high">Reach</option>
                  <option value="ultra">Ultra Reach</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Application Deadline</label>
                <input
                  type="date"
                  value={newProgram.deadline}
                  onChange={(e) => setNewProgram({ ...newProgram, deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
                <input
                  type="text"
                  value={newProgram.duration}
                  onChange={(e) => setNewProgram({ ...newProgram, duration: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., 6 weeks"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={newProgram.description}
                onChange={(e) => setNewProgram({ ...newProgram, description: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                rows="3"
                placeholder="What makes this program interesting?"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700"
              >
                Add Program
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-stone-300 text-stone-700 font-medium rounded-lg hover:bg-stone-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Programs List */}
      {programs.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No programs yet</h3>
          <p className="text-stone-600 mb-4">Start building your target list by adding programs you're interested in.</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700"
          >
            Add Your First Program
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {programs.map((program) => (
            <div key={program.id} className="bg-white border border-stone-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getCategoryIcon(program.category)}</span>
                  <div>
                    <h3 className="font-semibold text-slate-900">{program.name}</h3>
                    <p className="text-sm text-stone-600">{program.organization}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(program.status)}`}>
                    {program.status?.replace('-', ' ')}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getCompetitivenessColor(program.competitiveness)}`}>
                    {getCompetitivenessLabel(program.competitiveness)}
                  </span>
                </div>
              </div>
              
              {program.description && (
                <p className="text-sm text-stone-600 mb-3">{program.description}</p>
              )}
              
              <div className="flex items-center justify-between text-xs text-stone-500 mb-3">
                {program.duration && <span>{program.duration}</span>}
                {program.deadline && <span>Due: {new Date(program.deadline).toLocaleDateString()}</span>}
              </div>

              <div className="border-t border-stone-200 pt-3">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => updateProgram(program.id, { status: 'researching' })}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Research
                  </button>
                  <button
                    onClick={() => deleteProgram(program.id)}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Quick Add Modal */}
      <QuickAdd 
        isVisible={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onProgramAdded={(program) => {
          addProgram(program);
          setShowQuickAdd(false);
        }}
      />
    </div>
  );
};

export default ProgramCollection;